import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SwitchToggle from "../../components/SwitchToggle";
import {
  useLazyGetSubAdminQuery,
  useDeleteSubAdminMutation,
  useUpdateSubAdminMutation,
  useUpdateStatusMutation,
} from "../../services/subAdmin";
import { showError, showToast } from "../../constants/toasts";
import DeleteConfirmModal from "../../Modals/deleteConfirmModal";

const SubadminList = () => {
  const navigate = useNavigate();
  const [getSubAdmin, { data: subAdminData, isLoading }] =
    useLazyGetSubAdminQuery();
  const [deleteSubAdmin] = useDeleteSubAdminMutation();
  const [UpdateStatus] = useUpdateStatusMutation();
  const [subAdmins, setSubAdmins] = useState<any[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedSubAdmin, setSelectedSubAdmin] = useState<any>(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [start, setStart] = useState(0);
  const [length] = useState(10);

  const fetchSubAdmins = async () => {
    try {
      const response = await getSubAdmin({
        length: 10,
        start: page,
        search: debouncedSearchTerm,
      }).unwrap();
      if (response?.status === 200 || response?.statusCode === 200) {
        setSubAdmins(response.data || []);
      }
    } catch (error: any) {
      showError(error?.message || error?.data?.message);
    }
  };

  useEffect(() => {
    fetchSubAdmins();
  }, [search, start]);

  const handleDelete = async () => {
    if (!selectedSubAdmin) return;

    try {
      const response = await deleteSubAdmin({
        id: selectedSubAdmin.id,
      }).unwrap();
      if (response?.status === 200 || response?.statusCode === 200) {
        showToast("Subadmin deleted successfully");
        setDeleteModalOpen(false);
        setSelectedSubAdmin(null);
        fetchSubAdmins();
      }
    } catch (error: any) {
      showError(error?.message || error?.data?.message);
    }
  };

  const openDeleteModal = (subAdmin: any) => {
    setSelectedSubAdmin(subAdmin);
    setDeleteModalOpen(true);
  };

  const handleStatusUpdate = async (id: number, status: boolean) => {
    const body = {
      is_active: status,
    };

    try {
      const response = await UpdateStatus({ body, id }).unwrap();
      if (response?.status === 200) {
        showToast(
          `SubAdmin ${!status ? "deactivated" : "activated"} successfully`
        );
        fetchSubAdmins();
      } else {
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };
  const columns = [
    { id: "name", label: "Name" },
    { id: "email", label: "Email" },
    { id: "phone", label: "Phone Number" },
    { id: "role", label: "Role" },
    { id: "date", label: "Created Date" },
    { id: "status", label: "Status" },
    { id: "action", label: "Action" },
  ];

  return (
    <>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow hover>
              {columns.map((column) => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : subAdmins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No subadmins found
                </TableCell>
              </TableRow>
            ) : (
              subAdmins.map((subAdmin) => (
                <TableRow hover key={subAdmin.id}>
                  <TableCell>
                    <div
                      className="user_block click_action"
                      onClick={() =>
                        navigate(`/manage-subadmin/view/${subAdmin.id}`)
                      }
                    >
                      <figure>
                        <img
                          src={
                            subAdmin.profile_picture?.media_file_url ||
                            "/static/images/user_placeholder.png"
                          }
                          alt=""
                        />
                      </figure>
                      <p>
                        <b>{`${subAdmin.first_name || ""} ${
                          subAdmin.last_name || ""
                        }`}</b>
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>{subAdmin.email}</TableCell>
                  <TableCell>{subAdmin.phone_number}</TableCell>
                  <TableCell>{subAdmin.role?.role_name || "N/A"}</TableCell>
                  <TableCell>
                    {new Date(subAdmin.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <SwitchToggle
                      defaultChecked={subAdmin?.is_active ?? false}
                      onChange={(checked) =>
                        handleStatusUpdate(subAdmin.id, checked)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <div className="table_actions v2">
                      <Box>
                        <IconButton
                          onClick={() =>
                            navigate(`/manage-subadmin/view/${subAdmin.id}`)
                          }
                        >
                          <img src="/static/images/eye_icon.svg" alt="" />
                        </IconButton>
                      </Box>
                      <Box>
                        <IconButton
                          onClick={() =>
                            navigate(`/manage-subadmin/add/${subAdmin.id}`)
                          }
                        >
                          <img src="/static/images/edit2_icon.svg" alt="" />
                        </IconButton>
                      </Box>
                      <Box>
                        <IconButton onClick={() => openDeleteModal(subAdmin)}>
                          <img src="/static/images/trash2_icon.svg" alt="" />
                        </IconButton>
                      </Box>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteConfirmModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        handleDelete={handleDelete}
        title="Delete Subadmin"
        message={`Are you sure you want to delete ${selectedSubAdmin?.first_name} ${selectedSubAdmin?.last_name}?`}
      />
    </>
  );
};

export default SubadminList;
